import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import PageShell from '@/components/feature/PageShell';
import PageHero from '@/components/feature/PageHero';
import { hospitals } from '@/mocks/home';
import { fetchHospitalList } from '@/services/hospital';
import { adaptHospitals } from '@/services/adapters';
import { useRequest } from '@/hooks/useRequest';
import type { HospitalItem } from '@/types/content';

const HERO_IMAGE =
  'https://readdy.ai/api/search-image?query=Modern%20large%20integrated%20traditional%20Chinese%20and%20Western%20medicine%20hospital%20exterior%20at%20dusk%2C%20glass%20facade%20with%20warm%20beige%20stone%2C%20deep%20blue%20evening%20sky%2C%20soft%20architectural%20lighting%2C%20cinematic%20photography%20with%20dark%20lower%20area%20for%20text%20overlay&width=1920&height=560&seq=hospitals-hero&orientation=landscape';

export default function HospitalsPage() {
  const [keyword, setKeyword] = useState('');

  // 接口失败时降级为 mock 数据
  const { data: hospitalList } = useRequest<HospitalItem[]>(
    () => fetchHospitalList({ pageIndex: 1, pageSize: 999 }).then((res) => adaptHospitals(res.list)),
    { fallback: hospitals },
  );

  const filteredHospitals = useMemo(() => {
    const kw = keyword.trim().toLowerCase();
    if (!kw) return hospitalList;
    return hospitalList.filter((hospital) => hospital.name.toLowerCase().includes(kw));
  }, [keyword, hospitalList]);

  const hasKeyword = keyword.trim().length > 0;

  return (
    <PageShell>
      <PageHero
        image={HERO_IMAGE}
        eyebrow="医院专科"
        title="名院名科汇聚，中西医共护健康"
        description="协同全国著名三甲中医、中西医结合医院，推广医院知名医生、优势病种、特色方案、适宜技术、院内制剂等，推动优质医疗协同共享。"
        breadcrumb={[{ label: '首页', to: '/' }, { label: '医院专科' }]}
      />

      <section className="w-full px-4 md:px-8 py-12 md:py-16">
        <div className="max-w-7xl mx-auto">
          {/* 名称筛选栏 */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
            <div className="relative w-full md:max-w-md">
              <i className="ri-search-2-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-base"></i>
              <input
                type="text"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="输入医院名称筛选，如：广东省中医院"
                className="w-full pl-9 pr-9 py-2.5 text-sm rounded-md border border-background-300 bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-colors"
              />
              {hasKeyword && (
                <button
                  type="button"
                  onClick={() => setKeyword('')}
                  aria-label="清除筛选"
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center rounded-full text-foreground-400 hover:text-foreground-700 hover:bg-background-200/60 transition-colors cursor-pointer"
                >
                  <i className="ri-close-line text-base"></i>
                </button>
              )}
            </div>
            <p className="text-sm text-foreground-600 whitespace-nowrap">
              {hasKeyword ? (
                <>
                  筛选出 <span className="font-semibold text-primary-600">{filteredHospitals.length}</span> 家医院
                </>
              ) : (
                <>
                  共 <span className="font-semibold text-primary-600">{hospitalList.length}</span> 家合作医院
                </>
              )}
            </p>
          </div>

          {filteredHospitals.length > 0 ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 md:gap-6">
              {filteredHospitals.map((hospital) => (
                <article
                  key={hospital.id}
                  className="group flex flex-col sm:flex-row rounded-lg overflow-hidden bg-background-50 border border-background-200 hover:border-primary-300 transition-colors"
                >
                  <Link to={`/hospitals/${hospital.id}`} className="relative w-full sm:w-2/5 h-48 sm:h-auto overflow-hidden shrink-0 cursor-pointer">
                    <img
                      src={hospital.image}
                      alt={hospital.name}
                      title={`${hospital.name} - 合作医院`}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                  </Link>
                  <div className="flex flex-col flex-1 p-5">
                    <Link
                      to={`/hospitals/${hospital.id}`}
                      className="font-heading font-semibold text-base md:text-lg text-foreground-900 leading-snug hover:text-primary-600 transition-colors cursor-pointer"
                    >
                      {hospital.name}
                    </Link>
                    <p className="mt-2 text-xs md:text-sm text-foreground-600 leading-relaxed line-clamp-2">
                      {hospital.intro}
                    </p>

                    <div className="flex flex-wrap items-center gap-2 mt-3">
                      <span className="text-xs text-foreground-400">院内制剂</span>
                      {hospital.preparations.map((item) => (
                        <span
                          key={item.name}
                          className="px-2.5 py-1 rounded-full bg-secondary-100 text-secondary-800 text-xs"
                        >
                          {item.name}
                        </span>
                      ))}
                    </div>

                    <div className="mt-4 pt-4 border-t border-background-200 flex items-center justify-between gap-3">
                      <span className="inline-flex items-center gap-1.5 text-xs text-foreground-500">
                        <i className="ri-user-star-line text-primary-500"></i>
                        {hospital.doctors.length} 位合作医师
                      </span>
                      <Link
                        to={`/hospitals/${hospital.id}`}
                        className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-md border border-primary-200 text-primary-600 hover:bg-primary-50 transition-colors cursor-pointer whitespace-nowrap"
                      >
                        查看详情
                        <i className="ri-arrow-right-line"></i>
                      </Link>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-20 text-center">
              <div className="w-14 h-14 flex items-center justify-center rounded-full bg-background-100 text-foreground-400 mb-4">
                <i className="ri-search-eye-line text-2xl"></i>
              </div>
              <p className="text-foreground-800 font-medium">没有找到匹配的医院</p>
              <p className="mt-1.5 text-sm text-foreground-500">试试其他关键词，或查看全部合作医院。</p>
              <button
                type="button"
                onClick={() => setKeyword('')}
                className="mt-5 inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-md bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-refresh-line"></i>
                查看全部医院
              </button>
            </div>
          )}
        </div>
      </section>
    </PageShell>
  );
}